import { inject, Injectable } from '@angular/core'
import { AuthService } from '@core/services/auth.service'
import { UserService } from '@core/services/user.service'
import { AlertsService } from '@core/services/alerts.service'
import { NotificationSignalRService } from '@core/services/notification-signalr.service'

/**
 * AuthInitService
 * Initializes authentication state on application startup.
 * - Restores session and loads user profile for regular (login/password) users.
 * - Loads initial notifications for authenticated users (both regular and SSO).
 * - Instantiates NotificationSignalRService early so its effect runs at bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class AuthInitService {
  private readonly authService = inject(AuthService)
  private readonly userService = inject(UserService)
  private readonly alertsService = inject(AlertsService)
  // Injecting here ensures the singleton is created at bootstrap so its constructor
  // calls initialize() and the auth-monitoring effect starts immediately.
  // Do NOT call notificationService.initialize() again in initialize() — the
  // constructor already does it, and a second call would register a duplicate effect.
  private readonly notificationService = inject(NotificationSignalRService)

  initialize(): Promise<void> {
    try {
      if (this.authService.isAuthenticated()) {
        // Load regular-user profile (SSO users don't have a numeric userId)
        const user = this.authService.getCurrentUser()
        if (user?.userId) {
          // Fire-and-forget: store handles loading/error states
          this.userService.loadUser(user.userId).subscribe()
        }

        // Load initial notifications (works for both regular and SSO users)
        // Fire-and-forget: store handles loading/error states
        this.alertsService.loadInitialNotifications().subscribe()
      }

      // NOTE: SignalR connection is handled entirely by the effect registered in
      // NotificationSignalRService constructor. No explicit initialize() call needed.
    } catch (e) {
      // Ignore init errors to not block app bootstrap
    }
    return Promise.resolve()
  }
}
